import React, { useState, useMemo } from 'react';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import faqData from '../../../data/faqData.json';
import {
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

export default function FaqSection({ showTitle = true, limit = null }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('toutes');
  const [openItems, setOpenItems] = useState({});

  const categories = useMemo(() => {
    const cats = Array.from(new Set(faqData.map((item) => item.categorie)));
    return ['toutes', ...cats];
  }, []);

  const toggleAccordion = (id) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const filteredFaq = useMemo(() => {
    let list = faqData;

    if (selectedCategory !== 'toutes') {
      list = list.filter((item) => item.categorie === selectedCategory);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (item) =>
          item.question.toLowerCase().includes(term) ||
          item.reponse.toLowerCase().includes(term) ||
          item.categorie.toLowerCase().includes(term)
      );
    }

    if (limit && typeof limit === 'number') {
      list = list.slice(0, limit);
    }

    return list;
  }, [selectedCategory, searchTerm, limit]);

  // Schema.org FAQPage JSON-LD
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': filteredFaq.map((item) => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.reponse,
      },
    })),
  };

  return (
    <section className="w-full space-y-8 animate-fade-in" id="faq">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {showTitle && (
        <div className="text-center space-y-3 max-w-3xl mx-auto px-4">
          <h2 className="font-title text-2xl sm:text-4xl font-extrabold text-acem-primary-dark">
            Questions Fréquemment Posées
          </h2>
          <p className="text-xs sm:text-base text-acem-muted leading-relaxed">
            Retrouvez rapidement les réponses aux questions les plus courantes sur la vie étudiante, les inscriptions, la carte de séjour et l'association à Fès.
          </p>
        </div>
      )}

      {/* Barre de recherche et filtres de catégories */}
      <div className="max-w-4xl mx-auto space-y-4 px-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-acem-muted" aria-hidden="true" />
          <input
            type="text"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-acem-border bg-white text-acem-text text-sm focus:outline-none focus:ring-2 focus:ring-acem-primary/30 focus:border-acem-primary transition-all shadow-sm"
            placeholder="Rechercher une question (ex: carte de séjour, inscription, logement...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
          {categories.map((cat) => {
            const isActive = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-acem-primary/40 ${isActive
                  ? 'bg-acem-primary text-white shadow-sm scale-105'
                  : 'bg-white text-acem-text hover:text-acem-primary border border-acem-border hover:border-acem-primary/50 hover:bg-acem-surface-container-low'
                  }`}
              >
                {cat === 'toutes' ? 'Toutes les questions' : cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste des Accordions FAQ */}
      <div className="max-w-4xl mx-auto space-y-3 px-4">
        {filteredFaq.length === 0 ? (
          <Card variant="default" className="p-8 text-center text-acem-muted text-sm space-y-2">
            <p>Aucune question ne correspond à votre recherche.</p>
            <p className="text-xs italic">Essayez un autre mot-clé ou réinitialisez les filtres.</p>
          </Card>
        ) : (
          filteredFaq.map((item) => {
            const isOpen = !!openItems[item.id];
            return (
              <Card
                key={item.id}
                variant="elevated"
                className={`transition-all duration-200 border ${isOpen
                  ? 'border-acem-primary/40 shadow-acem-md bg-white'
                  : 'border-acem-border/80 hover:border-acem-primary/30'
                  }`}
              >
                <button
                  type="button"
                  onClick={() => toggleAccordion(item.id)}
                  className="w-full p-4 sm:p-5 flex items-start justify-between gap-4 text-left focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${isOpen ? 'bg-acem-primary text-white' : 'bg-acem-primary/10 text-acem-primary'
                      }`}>
                      <QuestionMarkCircleIcon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-acem-accent block mb-1">
                        {item.categorie}
                      </span>
                      <h3 className="font-title text-sm sm:text-base font-bold text-acem-primary-dark leading-snug">
                        {item.question}
                      </h3>
                    </div>
                  </div>
                  <ChevronDownIcon
                    className={`w-5 h-5 text-acem-muted flex-shrink-0 transition-transform duration-200 mt-1 ${isOpen ? 'rotate-180 text-acem-primary' : ''
                      }`}
                    aria-hidden="true"
                  />
                </button>

                {isOpen && (
                  <div className="px-4 pb-5 pt-0 sm:px-5 sm:pb-6 text-xs sm:text-sm text-acem-text/90 leading-relaxed border-t border-acem-border/40 mt-1 pt-3 animate-fade-in pl-15">
                    {item.reponse}
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* CTA Posez votre question */}
      <Card variant="glass" className="p-6 sm:p-8 text-center space-y-3 max-w-2xl mx-auto">
        <h3 className="font-title text-lg sm:text-xl font-bold text-acem-primary-dark">
          Vous n'avez pas trouvé la réponse à votre question ?
        </h3>
        <p className="text-xs sm:text-sm text-acem-muted leading-relaxed">
          Le bureau de l'ACEM Fès reste à votre disposition pour vous répondre et vous guider.
        </p>
        <div className="pt-2">
          <Button
            as="a"
            href="mailto:acemfes7@gmail.com"
            variant="primary"
            size="md"
            className="inline-flex items-center gap-2"
          >
            <EnvelopeIcon className="w-4 h-4" aria-hidden="true" />
            <span>Contactez notre équipe : acemfes7@gmail.com</span>
          </Button>
        </div>
      </Card>
    </section>
  );
}
